import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";
import { toast } from "react-toastify";

// Colour-band for each score value
const SCORE_META = {
  1:  { color: "#EF4444", bg: "#FEF2F2", label: "Very Poor"  },
  2:  { color: "#EF4444", bg: "#FEF2F2", label: "Poor"       },
  3:  { color: "#F97316", bg: "#FFF7ED", label: "Below Avg"  },
  4:  { color: "#F59E0B", bg: "#FFFBEB", label: "Average"    },
  5:  { color: "#EAB308", bg: "#FEFCE8", label: "Fair"       },
  6:  { color: "#84CC16", bg: "#F7FEE7", label: "Good"       },
  7:  { color: "#22C55E", bg: "#F0FDF4", label: "Very Good"  },
  8:  { color: "#10B981", bg: "#ECFDF5", label: "Excellent"  },
  9:  { color: "#1D9AEC", bg: "#EFF8FF", label: "Outstanding"},
  10: { color: "#6366F1", bg: "#EEF2FF", label: "Exceptional"},
};

// Evaluation criteria per nomination type
const ORG_CRITERIA = [
  {
    num: 1,
    title: "Quality of Care / Product Excellence",
    points: [
      "Hospitals: High standards in patient safety, outcomes, and care delivery",
      "Pharma: Proven efficacy, safety, and quality of products",
    ],
  },
  {
    num: 2,
    title: "Research, Development & Innovation",
    points: [
      "Hospitals: Clinical research and adoption of advanced medical technologies",
      "Pharma: Strong R&D pipeline, drug development, and scientific contribution",
    ],
  },
  {
    num: 3,
    title: "Education & Knowledge Dissemination",
    points: [
      "Hospitals: Training programs, academic affiliations, CME initiatives",
      "Pharma: Medical education, awareness programs, and scientific engagement",
    ],
  },
  {
    num: 4,
    title: "Ethics, Compliance & Healthcare Impact",
    points: [
      "Maintains strong ethical practices, regulatory compliance, transparency, and contributes to improving healthcare access and systems.",
    ],
  },
];

const INDIVIDUAL_CRITERIA = [
  {
    num: 1,
    title: "Strategic Leadership & Organizational Impact",
    desc: "Demonstrates strong leadership in driving institutional growth, efficiency, and long-term sustainability.",
  },
  {
    num: 2,
    title: "Innovation, Digital Transformation & Systems Improvement",
    desc: "Leads adoption of innovative solutions that improve healthcare delivery and accessibility.",
  },
  {
    num: 3,
    title: "Healthcare Access, Equity & Public Health Contribution",
    desc: "Contributes meaningfully to improving access, affordability, and equity in healthcare.",
  },
  {
    num: 4,
    title: "Governance, Ethics & Policy Influence",
    desc: "Upholds high standards of governance, transparency, and ethical leadership while influencing healthcare systems.",
  },
  {
    num: 5,
    title: "Talent Development & Organizational Culture",
    desc: "Builds strong teams and fosters a culture of excellence, accountability, and continuous learning.",
  },
];

function CriteriaPanel({ nominationType }) {
  const [open, setOpen] = useState(false);

  const isOrg = nominationType === "organisation";
  const criteria = isOrg ? ORG_CRITERIA : INDIVIDUAL_CRITERIA;
  const label    = isOrg
    ? "For Institutions (Hospitals / Pharma Companies)"
    : "For Non-Medical Professionals (Healthcare Leaders / Administrators / Policymakers)";

  if (!nominationType) return null;

  return (
    <div style={{
      border: "1px solid #E2E8F0", borderRadius: 10,
      overflow: "hidden", marginBottom: 14,
      boxShadow: "var(--shadow-xs)",
    }}>
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", background: open ? "#0F172A" : "#F8FAFC",
          border: "none", cursor: "pointer", transition: "background 0.2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: open ? "#fff" : "#374151" }}>
            Evaluation Criteria
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700,
            background: open ? "rgba(29,154,236,0.25)" : "#E2E8F0",
            color: open ? "#60C3F5" : "#64748B",
            padding: "2px 8px", borderRadius: 20,
          }}>
            {criteria.length} criteria
          </span>
        </div>
        <span style={{ fontSize: 14, color: open ? "#60C3F5" : "#94A3B8", fontWeight: 700 }}>
          {open ? "▴" : "▾"}
        </span>
      </button>

      {/* Expanded content */}
      {open && (
        <div style={{ padding: "14px 16px", background: "#fff", display: "flex", flexDirection: "column", gap: 10 }}>
          <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {label}
          </p>
          {criteria.map(c => (
            <div key={c.num} style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              padding: "10px 12px", borderRadius: 8,
              background: "#F8FAFC", border: "1px solid #E2E8F0",
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                background: "linear-gradient(135deg, #1D9AEC, #6366F1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 800, color: "#fff", marginTop: 1,
              }}>{c.num}</div>
              <div>
                <p style={{ margin: "0 0 4px", fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>
                  {c.title}
                </p>
                {c.desc && (
                  <p style={{ margin: 0, fontSize: 11.5, color: "#64748B", lineHeight: 1.55 }}>{c.desc}</p>
                )}
                {c.points && c.points.map((pt, i) => (
                  <p key={i} style={{ margin: i === 0 ? "4px 0 0" : "2px 0 0", fontSize: 11.5, color: "#64748B", lineHeight: 1.55 }}>
                    • {pt}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// CRM / internal fields that should never be shown to jury members
const INTERNAL_KEYS = new Set([
  "leadsource","leadtype","crmeventid","leadownerid","eventid","formid",
  "leadSource","leadType","crmEventId","leadOwnerId","eventId","formId",
  "zohoStatus","zohoLeadId","salutation",
]);

function buildLabelMap(eventForm) {
  const map = {};
  const all = [...(eventForm?.formTemplateId?.fields || []), ...(eventForm?.dynamicFields || [])];
  all.forEach(f => { map[f.fieldKey] = f.fieldLabel; });
  return map;
}

// Background research gathered by the internal team — read-only for jury
function ResearchPanel({ entries }) {
  if (!entries || entries.length === 0) return null;

  const icon = (t) => (t === "link" ? "🔗" : t === "document" ? "📄" : "📝");

  return (
    <div style={{ ...card, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: "#0F172A" }}>Team Research</h3>
        <span style={{ fontSize: 10, fontWeight: 700, background: "#EEF2FF", color: "#4F46E5", padding: "2px 8px", borderRadius: 20 }}>
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </span>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#64748B" }}>
        Background compiled by the VOH team to support your evaluation.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {entries.map((r) => (
          <div key={r._id} style={{ border: "1px solid #E2E8F0", borderRadius: 9, padding: "12px 14px", background: "#FBFCFD" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <span aria-hidden="true">{icon(r.type)}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A" }}>
                {r.title || (r.type === "note" ? "Research note" : r.type === "link" ? "Reference link" : "Document")}
              </span>
              {r.addedByName && (
                <span style={{ fontSize: 11, color: "#94A3B8" }}>
                  · {r.addedByName}{r.addedAt ? ` · ${new Date(r.addedAt).toLocaleDateString("en-IN")}` : ""}
                </span>
              )}
            </div>
            {r.type === "note" ? (
              <p style={{ margin: 0, fontSize: 13.5, color: "#1E293B", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {r.content}
              </p>
            ) : (
              <a href={r.content} target="_blank" rel="noreferrer"
                style={{ fontSize: 13.5, color: "#1D9AEC", fontWeight: 600, textDecoration: "none", wordBreak: "break-all" }}>
                {r.type === "document" ? (r.fileName || "Open document") : r.content} ↗
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldRow({ label, val }) {
  if (val === null || val === undefined || val === "") return null;
  const str = String(val);
  return (
    <div style={{ borderBottom: "1px solid #F8FAFC", paddingBottom: 12, marginBottom: 12 }}>
      <p style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 4px" }}>
        {label}
      </p>
      {str.startsWith("http") ? (
        <a href={str} target="_blank" rel="noreferrer"
          style={{ fontSize: 13.5, color: "#1D9AEC", fontWeight: 600, textDecoration: "none" }}>
          View document ↗
        </a>
      ) : (
        <p style={{ fontSize: 13.5, color: "#1E293B", margin: 0, lineHeight: 1.55, wordBreak: "break-word" }}>
          {typeof val === "boolean" ? (val ? "Yes" : "No") : str}
        </p>
      )}
    </div>
  );
}

export default function JuryScoringPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [myScore,    setMyScore]    = useState(null);
  const [score,      setScore]      = useState(0);
  const [remarks,    setRemarks]    = useState("");
  const [saving,          setSaving]          = useState(false);
  const [loadErr,         setLoadErr]         = useState(false);
  const [criteriaChecked, setCriteriaChecked] = useState(false);
  const [recusal,         setRecusal]         = useState(null);  // { reason } when recused
  const [recusalOpen,     setRecusalOpen]     = useState(false);
  const [recusalReason,   setRecusalReason]   = useState("");
  const [recusalSaving,   setRecusalSaving]   = useState(false);

  const FINALIZED = ["winner", "honorary", "not_selected"];

  useEffect(() => {
    Promise.all([
      api.get(`/jury/submission-detail/${submissionId}`),
      api.get(`/jury/scores/my/${submissionId}`),
    ]).then(([subRes, scoreRes]) => {
      setSubmission(subRes.data.data);
      const existing = scoreRes.data.data;
      if (existing) { setMyScore(existing); setScore(existing.score); setRemarks(existing.remarks || ""); }
    }).catch(() => { toast.error("Failed to load nomination"); setLoadErr(true); });
  }, [submissionId]);

  // Existing recusal for this nomination, if any
  useEffect(() => {
    if (!submission) return;
    const eventId = submission.eventId?._id || submission.eventId;
    if (!eventId) return;
    api.get(`/jury/recusals/my-event/${eventId}`)
      .then(res => {
        const mine = (res.data.data || []).find(r => String(r.submissionId) === String(submissionId));
        if (mine) setRecusal({ reason: mine.reason || "" });
      })
      .catch(() => {});
  }, [submission, submissionId]);

  const handleRecuse = async () => {
    setRecusalSaving(true);
    try {
      await api.post(`/jury/recusals/${submissionId}`, { reason: recusalReason });
      setRecusal({ reason: recusalReason });
      setRecusalOpen(false);
      setMyScore(null);
      setScore(0);
      toast.success("Recused — this nomination has left your queue");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not record the recusal");
    } finally { setRecusalSaving(false); }
  };

  const handleUndoRecusal = async () => {
    try {
      await api.delete(`/jury/recusals/${submissionId}`);
      setRecusal(null);
      setRecusalReason("");
      toast.success("Recusal withdrawn — you can score this nomination again");
    } catch { toast.error("Could not withdraw the recusal"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (score < 1 || score > 10) { toast.error("Please select a score from 1 to 10"); return; }
    setSaving(true);
    try {
      await api.post("/jury/scores", {
        submissionId,
        eventId: submission.eventId?._id || submission.eventId,
        score: Number(score),
        remarks,
      });
      toast.success(myScore ? "Score updated!" : "Score submitted!");
      navigate(-1);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.msg === "declarationRequired") {
        toast.error("Please accept the jury declaration first — signing in again will show it.");
      } else {
        toast.error(err.response?.data?.error || "Failed to submit score");
      }
    } finally { setSaving(false); }
  };

  if (loadErr) return (
    <Layout title="Error">
      <button onClick={() => navigate(-1)} style={{ marginBottom: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569" }}>← Back</button>
      <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>⚠️</div>
        <p style={{ fontWeight: 700, color: "#DC2626", margin: "0 0 6px" }}>Failed to load nomination</p>
        <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Please go back and try again.</p>
      </div>
    </Layout>
  );

  if (!submission) return (
    <Layout title="Loading…">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20 }}>
        {[1, 2].map(i => (
          <div key={i} style={card}>
            <div className="skeleton" style={{ height: 14, width: "50%", marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 10, width: "70%", marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 10, width: "60%" }} />
          </div>
        ))}
      </div>
    </Layout>
  );

  const labelMap = buildLabelMap(submission.eventFormId);
  const fields   = Object.entries(submission.submissionData || {}).filter(([k]) => {
    if (k.startsWith("_")) return false;
    const norm = k.toLowerCase().replace(/[\s_]/g, "");
    return !INTERNAL_KEYS.has(k) && !INTERNAL_KEYS.has(norm);
  });
  const attendees = submission.attendees || [];
  const scoreMeta = score > 0 ? SCORE_META[score] : null;

  return (
    <Layout title="Score Nomination">

      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: 20, background: "#fff", border: "1px solid #E2E8F0",
          borderRadius: 8, padding: "7px 16px", cursor: "pointer",
          fontSize: 13, fontWeight: 600, color: "#475569",
          display: "inline-flex", alignItems: "center", gap: 6,
          boxShadow: "var(--shadow-xs)",
        }}
      >← Back</button>

      {/* Header bar */}
      <div style={{
        background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
        padding: "14px 20px", marginBottom: 20,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        boxShadow: "var(--shadow-xs)",
      }}>
        {submission.eventFormId?.formName && (
          <span style={chip("blue")}>{submission.eventFormId.formName}</span>
        )}
        {submission.nominationCategory && (
          <span style={chip("purple")}>{submission.nominationCategory}</span>
        )}
        {submission.nominationSubCategory && (
          <span style={chip("purple")}>{submission.nominationSubCategory}</span>
        )}
        {submission.eventId?.eventName && (
          <span style={{ fontSize: 12.5, color: "#64748B" }}>
            Event: <strong style={{ color: "#0F172A" }}>{submission.eventId.eventName}</strong>
          </span>
        )}
      </div>

      <div className="voh-split" style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20, alignItems: "start" }}>

        {/* Left — nomination details */}
        <div>
          <div style={card}>
            <h3 style={{ margin: "0 0 18px", fontSize: 14.5, fontWeight: 700, color: "#0F172A" }}>
              {attendees.length > 0 ? "Primary Nominee" : "Nominee Details"}
            </h3>
            <div className="voh-fields" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
              {fields.map(([key, val]) => (
                <FieldRow
                  key={key}
                  label={labelMap[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}
                  val={val}
                />
              ))}
            </div>
          </div>

          <ResearchPanel entries={submission.researchNotes} />

          {attendees.map((att, idx) => (
            <div key={idx} style={{ ...card, marginTop: 16 }}>
              <h3 style={{ margin: "0 0 18px", fontSize: 14.5, fontWeight: 700, color: "#0F172A" }}>
                Attendee {idx + 2}
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                {Object.entries(att || {}).filter(([k]) => !k.startsWith("_")).map(([key, val]) => (
                  <FieldRow
                    key={key}
                    label={labelMap[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}
                    val={val}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right — scoring panel (sticky) */}
        <div className="voh-sticky-panel" style={{ position: "sticky", top: 80 }}>

          {/* Evaluation criteria reference panel */}
          <CriteriaPanel nominationType={submission.eventFormId?.nominationType} />

          {/* Recused — scoring is closed for this nominee */}
          {recusal && (
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
              <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#92400E", fontSize: 13 }}>
                You recused yourself from this nomination
              </p>
              {recusal.reason && (
                <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "#78350F", lineHeight: 1.5 }}>{recusal.reason}</p>
              )}
              <p style={{ margin: "0 0 10px", fontSize: 12, color: "#92400E", opacity: 0.85 }}>
                It no longer counts towards your pending list or the panel quorum.
              </p>
              <button type="button" onClick={handleUndoRecusal}
                style={{ background: "#fff", border: "1px solid #FDE68A", color: "#92400E", borderRadius: 8, padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Withdraw recusal
              </button>
            </div>
          )}

          {/* Already scored callout */}
          {myScore && (
            <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10, padding: "12px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>✅</span>
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: "#059669", fontSize: 13 }}>You scored this: {myScore.score}/10</p>
                <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>You can update your score below.</p>
              </div>
            </div>
          )}

          <div style={{
            ...card,
            borderTop: scoreMeta ? `3px solid ${scoreMeta.color}` : "3px solid #E2E8F0",
            transition: "border-color 0.3s",
            opacity: recusal ? 0.45 : 1,
            pointerEvents: recusal ? "none" : "auto",
          }} aria-hidden={recusal ? "true" : undefined}>

            <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
              {myScore ? "Update Your Score" : "Submit Your Score"}
            </h3>
            {myScore && (
              <p style={{ fontSize: 12.5, color: "#64748B", margin: "0 0 18px" }}>
                Your previous score: <strong style={{ color: "#0F172A" }}>{myScore.score}/10</strong>
              </p>
            )}

            {/* Score feedback banner */}
            {scoreMeta && (
              <div style={{
                background: scoreMeta.bg, border: `1px solid ${scoreMeta.color}30`,
                borderRadius: 8, padding: "10px 14px",
                marginBottom: 18, marginTop: myScore ? 0 : 16,
                display: "flex", alignItems: "center", gap: 10,
                transition: "all 0.25s",
              }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: scoreMeta.color }}>{score}</span>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: scoreMeta.color }}>
                    {scoreMeta.label}
                  </span>
                  <p style={{ fontSize: 11, color: scoreMeta.color, opacity: 0.7, margin: 0 }}>out of 10</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <label style={lbl}>Select Score (1 – 10)</label>
              <div className="voh-score-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 7, marginBottom: 22 }}>
                {[1,2,3,4,5,6,7,8,9,10].map(n => {
                  const m       = SCORE_META[n];
                  const active  = score === n;
                  return (
                    <button
                      key={n} type="button"
                      onClick={() => setScore(n)}
                      style={{
                        height: 46, borderRadius: 9,
                        border: active ? `2px solid ${m.color}` : "2px solid #E2E8F0",
                        background: active ? m.bg : "#F8FAFC",
                        color: active ? m.color : "#94A3B8",
                        fontWeight: active ? 800 : 600,
                        fontSize: 16, cursor: "pointer",
                        transition: "all 0.15s",
                        boxShadow: active ? `0 2px 8px ${m.color}30` : "none",
                        transform: active ? "scale(1.05)" : "scale(1)",
                      }}
                    >{n}</button>
                  );
                })}
              </div>

              <label style={lbl}>Remarks <span style={{ color: "#94A3B8", fontWeight: 500 }}>(optional)</span></label>
              <textarea
                value={remarks} onChange={e => setRemarks(e.target.value)}
                rows={4} placeholder="Share your assessment of this nomination…"
                style={{
                  width: "100%", padding: "10px 13px",
                  border: "1.5px solid #E2E8F0", borderRadius: 8,
                  fontSize: 13.5, resize: "vertical",
                  boxSizing: "border-box", fontFamily: "inherit",
                  color: "#0F172A", lineHeight: 1.55,
                  transition: "border-color 0.15s",
                  outline: "none",
                }}
                onFocus={e => { e.target.style.borderColor = "#1D9AEC"; }}
                onBlur={e =>  { e.target.style.borderColor = "#E2E8F0"; }}
              />

              {/* Criteria acknowledgement checkbox */}
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                marginTop: 18, cursor: "pointer",
                padding: "12px 14px", borderRadius: 9,
                background: criteriaChecked ? "#F0FDF4" : "#FFFBEB",
                border: `1.5px solid ${criteriaChecked ? "#A7F3D0" : "#FDE68A"}`,
                transition: "all 0.2s",
              }}>
                <input
                  type="checkbox"
                  checked={criteriaChecked}
                  onChange={e => setCriteriaChecked(e.target.checked)}
                  style={{ width: 16, height: 16, marginTop: 1, accentColor: "#10B981", flexShrink: 0, cursor: "pointer" }}
                />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: criteriaChecked ? "#065F46" : "#92400E", lineHeight: 1.5 }}>
                  I confirm that I am evaluating this nomination based on the defined criteria above.
                </span>
              </label>

              <button
                type="submit"
                disabled={saving || score === 0 || !criteriaChecked}
                style={{
                  marginTop: 12, width: "100%", padding: "13px",
                  background: (saving || score === 0 || !criteriaChecked)
                    ? "#E2E8F0"
                    : scoreMeta
                      ? `linear-gradient(135deg, ${scoreMeta.color}, ${scoreMeta.color}cc)`
                      : "linear-gradient(135deg, #1D9AEC, #1585D8)",
                  color: (saving || score === 0 || !criteriaChecked) ? "#94A3B8" : "#fff",
                  border: "none", borderRadius: 9,
                  fontWeight: 700, fontSize: 14.5,
                  cursor: (saving || score === 0 || !criteriaChecked) ? "not-allowed" : "pointer",
                  boxShadow: (saving || score === 0 || !criteriaChecked) ? "none" : `0 4px 14px ${scoreMeta?.color || "#1D9AEC"}40`,
                  transition: "all 0.25s",
                  letterSpacing: "-0.01em",
                }}
              >
                {saving ? "Submitting…" : score === 0 ? "Select a score above" : !criteriaChecked ? "Confirm criteria above to proceed" : myScore ? "Update Score" : "Submit Score"}
              </button>
            </form>
          </div>

          {/* Conflict of interest */}
          {!recusal && (
            <div style={{ marginTop: 14 }}>
              {recusalOpen ? (
                <div style={{ ...card, padding: 18, borderTop: "3px solid #F59E0B" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                    Declare a conflict of interest
                  </h3>
                  <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "#64748B", lineHeight: 1.5 }}>
                    This nomination leaves your queue and stops counting towards the panel quorum. You can withdraw it later.
                  </p>
                  <label style={lbl} htmlFor="recusal-reason">Reason <span style={{ color: "#94A3B8", fontWeight: 500 }}>(optional)</span></label>
                  <textarea
                    id="recusal-reason"
                    value={recusalReason}
                    onChange={e => setRecusalReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. I serve on the board of this organisation"
                    style={{ width: "100%", padding: "10px 13px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13.5, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit", color: "#0F172A", outline: "none" }}
                  />
                  <div style={{ display: "flex", gap: 9, marginTop: 12 }}>
                    <button type="button" onClick={handleRecuse} disabled={recusalSaving}
                      style={{ flex: 1, padding: "10px", background: "#B45309", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: recusalSaving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                      {recusalSaving ? "Recording…" : "Confirm recusal"}
                    </button>
                    <button type="button" onClick={() => setRecusalOpen(false)}
                      style={{ padding: "10px 16px", background: "#fff", color: "#475569", border: "1px solid #E2E8F0", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setRecusalOpen(true)}
                  style={{ width: "100%", padding: "11px", background: "transparent", color: "#92400E", border: "1.5px solid #FDE68A", borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  I have a conflict of interest
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const card = {
  background: "#fff", borderRadius: 12, padding: 24,
  boxShadow: "var(--shadow-sm)", border: "1px solid #E2E8F0",
};
const lbl  = { display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10, letterSpacing: "-0.01em" };
const chip = (color) => ({
  display: "inline-block", padding: "4px 12px", borderRadius: 20,
  fontSize: 11, fontWeight: 600,
  background: color === "blue" ? "#EFF8FF" : "#F5F3FF",
  color:      color === "blue" ? "#0369A1" : "#6D28D9",
  border: `1px solid ${color === "blue" ? "#BFDBFE" : "#DDD6FE"}`,
});
